import { useTranslation } from "react-i18next";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { CheckCheck, Clock, XCircle, Ban } from "lucide-react";
import { NODE_HEIGHT } from "./types";

interface EndNodeData {
  allApproved?: boolean;
  instanceStatus?: string;
  approvedCount?: number;
  totalSteps?: number;
  [key: string]: unknown;
}

type EndState = "approved" | "rejected" | "cancelled" | "in_progress";

const STATE_CONFIG: Record<
  EndState,
  {
    border: string;
    headerBg: string;
    headerText: string;
    iconBg: string;
    icon: React.ReactNode;
    label: string;
    title: string;
    /** Returns an i18next key + params — the node resolves it with `t`.
     *  Params are named, never positional `t`, which would shadow the
     *  translator. */
    subtitle: (
      approvedCount: number,
      totalSteps: number,
    ) => { key: string; params: Record<string, number> };
    opacity: string;
  }
> = {
  approved: {
    border: "border-green-400",
    headerBg: "bg-green-500",
    headerText: "text-white",
    iconBg: "bg-green-50",
    icon: <CheckCheck className="size-6 text-green-500" />,
    label: "common:status.completed",
    title: "common:workflow.flow_completed",
    subtitle: (_approved, total) => ({
      key: "common:workflow.all_steps_approved",
      params: { count: total },
    }),
    opacity: "",
  },
  rejected: {
    border: "border-red-400",
    headerBg: "bg-red-500",
    headerText: "text-white",
    iconBg: "bg-red-50",
    icon: <XCircle className="size-6 text-red-500" />,
    label: "common:status.rejected",
    title: "common:workflow.flow_rejected",
    subtitle: (approved, total) => ({
      key: "common:workflow.n_of_m_steps_approved",
      params: { approved, count: total },
    }),
    opacity: "",
  },
  cancelled: {
    border: "border-gray-300",
    headerBg: "bg-border",
    headerText: "text-gray-500",
    iconBg: "bg-gray-100",
    icon: <Ban className="size-6 text-gray-400" />,
    label: "common:status.cancelled",
    title: "common:workflow.flow_cancelled",
    subtitle: (approved, total) => ({
      key: "common:workflow.n_of_m_steps_approved",
      params: { approved, count: total },
    }),
    opacity: "opacity-60",
  },
  in_progress: {
    border: "border-border",
    headerBg: "bg-gray-100",
    headerText: "text-gray-400",
    iconBg: "bg-gray-100",
    icon: <Clock className="size-6 text-gray-400" />,
    label: "common:status.pending",
    title: "common:workflow.end_of_approval_flow",
    subtitle: (approved, total) => ({
      key: "common:workflow.n_of_m_steps_approved",
      params: { approved, count: total },
    }),
    opacity: "opacity-50",
  },
};

// Progress-pill fill per step state.
const PILL_COLOR = {
  approved: "bg-green-400",
  rejected: "bg-red-400",
  pending: "bg-border",
} as const;

const resolveState = (
  instanceStatus?: string,
  allApproved?: boolean,
): EndState => {
  if (allApproved || instanceStatus === "approved") return "approved";
  if (instanceStatus === "rejected") return "rejected";
  if (instanceStatus === "cancelled") return "cancelled";
  return "in_progress";
};

const EndNode = ({ data }: NodeProps) => {
  const {
    allApproved,
    instanceStatus,
    approvedCount = 0,
    totalSteps = 0,
  } = data as EndNodeData;
  const { t } = useTranslation(["common"]);
  const state = resolveState(instanceStatus, allApproved);
  const cfg = STATE_CONFIG[state];
  const subtitle = cfg.subtitle(approvedCount, totalSteps);

  return (
    <div
      className={`border rounded-xl w-[372px] bg-white shadow-xs overflow-hidden flex flex-col ${cfg.border} ${cfg.opacity}`}
      style={{ height: NODE_HEIGHT }}
    >
      {/* Header */}
      <div
        className={`flex items-center justify-between px-4 py-2 shrink-0 ${cfg.headerBg}`}
      >
        <span
          className={`text-xs font-semibold uppercase tracking-wide ${cfg.headerText}`}
        >
          {t("common:workflow.end_node")}
        </span>
        <span className={`text-xs font-medium ${cfg.headerText}`}>
          {t(cfg.label)}
        </span>
      </div>

      {/* Body */}
      <div className="flex flex-col items-center justify-center gap-4 px-4 py-3 flex-1 min-h-0">
        <div
          className={`size-14 rounded-full flex items-center justify-center shrink-0 ${cfg.iconBg}`}
        >
          {cfg.icon}
        </div>

        <div className="text-center space-y-1">
          <p
            className={`text-sm font-bold ${
              state === "approved"
                ? "text-green-600"
                : state === "rejected"
                  ? "text-red-600"
                  : "text-gray-400"
            }`}
          >
            {t(cfg.title)}
          </p>
          <p
            className={`text-xs ${
              state === "approved"
                ? "text-green-500"
                : state === "rejected"
                  ? "text-red-400"
                  : "text-gray-400"
            }`}
          >
            {t(subtitle.key, subtitle.params)}
          </p>
        </div>

        {/* Step progress pills */}
        {totalSteps > 0 && (
          <div className="flex items-center gap-1.5">
            {Array.from({ length: totalSteps }).map((_, i) => {
              let color: string;
              if (i < approvedCount) {
                color = PILL_COLOR.approved;
              } else if (state === "rejected" && i === approvedCount) {
                color = PILL_COLOR.rejected;
              } else {
                color = PILL_COLOR.pending;
              }
              return (
                <div key={i} className={`h-2 w-10 rounded-full ${color}`} />
              );
            })}
          </div>
        )}
      </div>

      <Handle
        type="target"
        position={Position.Top}
        className="bg-white! border-2! border-primary! w-3! h-3!"
        isConnectable={false}
      />
    </div>
  );
};

export default EndNode;
